import { z } from 'zod';

import { MIN_AGE, SQUAD_DEFAULTS } from '../constants/squad';
import {
  CompositionSchema,
  LevelSchema,
  PoolStatusSchema,
  ProposalStatusSchema,
  RoleSchema,
  SquadStatusSchema,
  WeekdaySchema,
} from '../enums';
import { HHmmSchema, IdSchema, KopecksSchema } from './common';

/**
 * Входные схемы админки (BLUEPRINT Stage 4). Валидируются и формой в `apps/admin`,
 * и процедурами `admin.*` на сервере (4.4: одни схемы для web и API).
 */

const SizeSchema = z
  .number()
  .int()
  .min(SQUAD_DEFAULTS.proposalSizeMin)
  .max(SQUAD_DEFAULTS.proposalSizeMax);

const AgeSchema = z.number().int().min(MIN_AGE).max(99);

/** Полоса возраста 1–5 (`AGE_BANDS`). */
export const AgeBandSchema = z.number().int().min(1).max(5);

// ───────────────────────────── пул ─────────────────────────────

export const AdminPoolListInputSchema = z.object({
  cityId: IdSchema.optional(),
  activityId: IdSchema.optional(),
  squadSize: SizeSchema.optional(),
  /** Кандидат подходит, если свободен хотя бы в один из этих дней */
  weekdays: z.array(WeekdaySchema).max(7).optional(),
  levelBand: LevelSchema.optional(),
  ageBand: AgeBandSchema.optional(),
  districtId: IdSchema.optional(),
  status: PoolStatusSchema.default('WAITING'),
});
export type AdminPoolListInput = z.infer<typeof AdminPoolListInputSchema>;

// ───────────────────────────── отряды ─────────────────────────────

const SquadFieldsSchema = z.object({
  name: z.string().trim().min(2).max(40).nullable(),
  composition: CompositionSchema,
  weekday: WeekdaySchema,
  startTime: HHmmSchema,
  levelBand: LevelSchema,
  ageMin: AgeSchema,
  ageMax: AgeSchema,
  districtIds: z.array(IdSchema).max(12),
  /** Переопределение `max(ceil(capacity × 0.75), minPlay)`; null — считать автоматически */
  minToLaunch: z.number().int().min(1).max(SQUAD_DEFAULTS.proposalSizeMax).nullable(),
  /** null — взять `activity.reserveSlots` */
  reserveCapacity: z.number().int().min(0).max(4).nullable(),
});

function ageRangeOk(v: { ageMin: number; ageMax: number }): boolean {
  return v.ageMin <= v.ageMax;
}
const AGE_RANGE_MSG = { message: 'Возраст «от» не больше «до»', path: ['ageMax'] };

export const AdminSquadCreateInputSchema = SquadFieldsSchema.extend({
  cityId: IdSchema,
  primaryActivityId: IdSchema,
  /** = выбранный размер из `Activity.sizeOptions` */
  capacity: SizeSchema,
  name: SquadFieldsSchema.shape.name.default(null),
  composition: CompositionSchema.default('MEN'),
  minToLaunch: SquadFieldsSchema.shape.minToLaunch.default(null),
  reserveCapacity: SquadFieldsSchema.shape.reserveCapacity.default(null),
  /** Кандидаты из пула, которых сразу добавить в состав */
  memberUserIds: z.array(IdSchema).max(SQUAD_DEFAULTS.proposalSizeMax).default([]),
  captainUserId: IdSchema.nullable().default(null),
})
  .refine(ageRangeOk, AGE_RANGE_MSG)
  .refine((v) => new Set(v.memberUserIds).size === v.memberUserIds.length, {
    message: 'Участник указан дважды',
    path: ['memberUserIds'],
  })
  .refine((v) => v.memberUserIds.length <= v.capacity, {
    message: 'Больше людей, чем мест',
    path: ['memberUserIds'],
  })
  .refine((v) => !v.captainUserId || v.memberUserIds.includes(v.captainUserId), {
    message: 'Капитан должен быть в составе',
    path: ['captainUserId'],
  });
export type AdminSquadCreateInput = z.infer<typeof AdminSquadCreateInputSchema>;

export const AdminSquadUpdateInputSchema = SquadFieldsSchema.partial()
  .extend({
    squadId: IdSchema,
    /**
     * Раунд 4, O4: вечер или время отряда меняются, а сборы живого сезона остаются на своих
     * датах («только отряд» — новый вечер со следующего сезона). Без флага сервер отвечает
     * `season_schedule_mismatch`, пока сборы не перенесены в конструкторе сезона.
     */
    keepSeasonSchedule: z.boolean().optional(),
  })
  .refine((v) => v.ageMin === undefined || v.ageMax === undefined || ageRangeOk({ ageMin: v.ageMin, ageMax: v.ageMax }), AGE_RANGE_MSG);
export type AdminSquadUpdateInput = z.infer<typeof AdminSquadUpdateInputSchema>;

export const AdminSquadsListInputSchema = z.object({
  cityId: IdSchema.optional(),
  status: z.array(SquadStatusSchema).max(5).optional(),
});
export type AdminSquadsListInput = z.infer<typeof AdminSquadsListInputSchema>;

export const AdminSquadIdInputSchema = z.object({ squadId: IdSchema });

export const AdminSquadAddMemberInputSchema = z.object({
  squadId: IdSchema,
  userId: IdSchema,
  isReserve: z.boolean().default(false),
});
export type AdminSquadAddMemberInput = z.infer<typeof AdminSquadAddMemberInputSchema>;

export const AdminSquadRemoveMemberInputSchema = z.object({
  squadId: IdSchema,
  userId: IdSchema,
  /** Вернуть заявку в пул (WAITING) */
  returnToPool: z.boolean().default(true),
});
export type AdminSquadRemoveMemberInput = z.infer<typeof AdminSquadRemoveMemberInputSchema>;

export const AdminSquadSetCaptainInputSchema = z.object({ squadId: IdSchema, userId: IdSchema });

/** Telegram chat id — BigInt, в JSON ходит строкой (может быть отрицательным у групп). */
export const TgChatIdSchema = z.string().regex(/^-?\d{1,20}$/, 'Chat ID — число');

export const AdminSquadBindChatInputSchema = z.object({
  squadId: IdSchema,
  tgChatId: TgChatIdSchema.nullable(),
  tgInviteLink: z.string().url().max(200).nullable(),
});
export type AdminSquadBindChatInput = z.infer<typeof AdminSquadBindChatInputSchema>;

export const AdminSquadDisbandInputSchema = z.object({
  squadId: IdSchema,
  returnToPool: z.boolean().default(true),
});

// ───────────────────────────── площадки ─────────────────────────────

export const OffPeakWindowSchema = z
  .object({
    weekday: WeekdaySchema,
    from: HHmmSchema,
    to: HHmmSchema,
    discountPct: z.number().int().min(0).max(100),
  })
  .refine((w) => w.from < w.to, { message: 'Окно: начало раньше конца', path: ['to'] });
export type OffPeakWindow = z.infer<typeof OffPeakWindowSchema>;

export const VenueFieldsSchema = z.object({
  cityId: IdSchema,
  name: z.string().trim().min(2, 'Название от 2 символов').max(80),
  address: z.string().trim().min(3, 'Укажи адрес').max(200),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  activityIds: z.array(IdSchema).max(20),
  contact: z.string().trim().max(200).nullable(),
  pricePerHourKopecks: KopecksSchema.nullable(),
  offPeak: z.array(OffPeakWindowSchema).max(14),
  capacity: z.number().int().min(1).max(200).nullable(),
  notes: z.string().trim().max(1000).nullable(),
  isPartner: z.boolean(),
  isActive: z.boolean(),
});

export const AdminVenueCreateInputSchema = VenueFieldsSchema.extend({
  contact: VenueFieldsSchema.shape.contact.default(null),
  pricePerHourKopecks: VenueFieldsSchema.shape.pricePerHourKopecks.default(null),
  offPeak: VenueFieldsSchema.shape.offPeak.default([]),
  capacity: VenueFieldsSchema.shape.capacity.default(null),
  notes: VenueFieldsSchema.shape.notes.default(null),
  isPartner: z.boolean().default(false),
  isActive: z.boolean().default(true),
});
export type AdminVenueCreateInput = z.infer<typeof AdminVenueCreateInputSchema>;

export const AdminVenueUpdateInputSchema = VenueFieldsSchema.omit({ cityId: true })
  .partial()
  .extend({ venueId: IdSchema });
export type AdminVenueUpdateInput = z.infer<typeof AdminVenueUpdateInputSchema>;

export const AdminVenuesListInputSchema = z.object({
  cityId: IdSchema.optional(),
  activityId: IdSchema.optional(),
  includeInactive: z.boolean().default(false),
});

// ───────────────────────────── активности ─────────────────────────────

export const ActivityCodeSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{1,39}$/, 'Код: латиница, цифры и дефис');

export const ActivityFieldsSchema = z.object({
  name: z.string().trim().min(2, 'Название от 2 символов').max(40),
  description: z.string().trim().max(500).nullable(),
  isPrimary: z.boolean(),
  sizeOptions: z
    .array(SizeSchema)
    .max(5)
    .refine((arr) => new Set(arr).size === arr.length, 'Размеры не должны повторяться')
    .transform((arr) => [...arr].sort((a, b) => a - b)),
  minPlay: z.number().int().min(1).max(32),
  defaultDurationMin: z.number().int().min(15).max(600),
  indoor: z.boolean(),
  needsLevel: z.boolean(),
  isActive: z.boolean(),
  /** Пусто — во всех городах */
  cityIds: z.array(IdSchema).max(50),
  seasonPriceKopecks: KopecksSchema.nullable(),
  reserveSlots: z.number().int().min(0).max(4),
  reservePriceRatio: z.number().min(0.1).max(1),
  rotationHint: z.string().trim().max(200).nullable(),
  venuePending: z.boolean(),
});

const PRIMARY_NEEDS_SIZES = {
  message: 'У основной активности нужен хотя бы один размер отряда',
  path: ['sizeOptions'],
};

export const AdminActivityCreateInputSchema = ActivityFieldsSchema.extend({
  code: ActivityCodeSchema,
  description: ActivityFieldsSchema.shape.description.default(null),
  isPrimary: z.boolean().default(true),
  indoor: z.boolean().default(true),
  needsLevel: z.boolean().default(false),
  isActive: z.boolean().default(true),
  cityIds: ActivityFieldsSchema.shape.cityIds.default([]),
  seasonPriceKopecks: ActivityFieldsSchema.shape.seasonPriceKopecks.default(null),
  reserveSlots: ActivityFieldsSchema.shape.reserveSlots.default(0),
  reservePriceRatio: ActivityFieldsSchema.shape.reservePriceRatio.default(1),
  rotationHint: ActivityFieldsSchema.shape.rotationHint.default(null),
  venuePending: z.boolean().default(false),
}).refine((v) => !v.isPrimary || v.sizeOptions.length > 0, PRIMARY_NEEDS_SIZES);
export type AdminActivityCreateInput = z.infer<typeof AdminActivityCreateInputSchema>;

export const AdminActivityUpdateInputSchema = ActivityFieldsSchema.partial().extend({
  activityId: IdSchema,
});
export type AdminActivityUpdateInput = z.infer<typeof AdminActivityUpdateInputSchema>;

export const AdminActivitiesListInputSchema = z.object({
  cityId: IdSchema.optional(),
  includeInactive: z.boolean().default(true),
});

// ───────────────────────────── предложения ─────────────────────────────

export const AdminProposalsListInputSchema = z.object({
  cityId: IdSchema.optional(),
  status: ProposalStatusSchema.optional(),
});

/** Новая активность из предложения: город берётся из предложения, `source = PROPOSED`. */
export const ProposalActivityFieldsSchema = ActivityFieldsSchema.omit({
  cityIds: true,
  isActive: true,
  venuePending: true,
}).extend({ code: ActivityCodeSchema });

export const AdminProposalApproveInputSchema = z
  .object({
    proposalId: IdSchema,
    activity: ProposalActivityFieldsSchema,
    /** Площадки города, к которым привязать активность */
    venueIds: z.array(IdSchema).max(20).default([]),
    /** «Площадку ищем» — можно подтвердить без площадки */
    venuePending: z.boolean().default(false),
    /** Размер отряда для заявки автора; null — первый из sizeOptions */
    authorSquadSize: SizeSchema.nullable().default(null),
    opsComment: z.string().trim().max(500).nullable().default(null),
  })
  .refine((v) => !v.activity.isPrimary || v.activity.sizeOptions.length > 0, {
    ...PRIMARY_NEEDS_SIZES,
    path: ['activity', 'sizeOptions'],
  })
  .refine((v) => v.venueIds.length > 0 || v.venuePending, {
    message: 'Привяжи площадку или отметь «площадку ищем»',
    path: ['venueIds'],
  })
  .refine(
    (v) => v.authorSquadSize === null || v.activity.sizeOptions.includes(v.authorSquadSize),
    { message: 'Размер для автора должен быть из списка размеров', path: ['authorSquadSize'] },
  );
export type AdminProposalApproveInput = z.infer<typeof AdminProposalApproveInputSchema>;

export const AdminProposalMergeInputSchema = z.object({
  proposalId: IdSchema,
  activityId: IdSchema,
  authorSquadSize: SizeSchema.nullable().default(null),
  opsComment: z.string().trim().max(500).nullable().default(null),
});
export type AdminProposalMergeInput = z.infer<typeof AdminProposalMergeInputSchema>;

export const AdminProposalRejectInputSchema = z.object({
  proposalId: IdSchema,
  opsComment: z.string().trim().min(3, 'Напиши автору причину').max(500),
});
export type AdminProposalRejectInput = z.infer<typeof AdminProposalRejectInputSchema>;

// ───────────────────────────── пользователи ─────────────────────────────

export const AdminUserIdInputSchema = z.object({ userId: IdSchema });

export const AdminUserSetRoleInputSchema = z.object({ userId: IdSchema, role: RoleSchema });

export const AdminUserSetStatusInputSchema = z.object({
  userId: IdSchema,
  status: z.enum(['ACTIVE', 'BANNED']),
  reason: z.string().trim().max(500).nullable().default(null),
});

export const AdminUserSetLevelInputSchema = z.object({
  userId: IdSchema,
  activityId: IdSchema,
  /** null — снять корректировку, вернуть самооценку */
  adjusted: LevelSchema.nullable(),
});

export const AdminUserSetNotesInputSchema = z.object({
  userId: IdSchema,
  opsNotes: z.string().trim().max(2000).nullable(),
});

// ───────────────────────────── журнал ─────────────────────────────

export const AdminAuditListInputSchema = z.object({
  entity: z.string().max(40).optional(),
  entityId: z.string().max(64).optional(),
  actorId: IdSchema.optional(),
  limit: z.number().int().min(1).max(200).default(50),
});
export type AdminAuditListInput = z.infer<typeof AdminAuditListInputSchema>;
